import posthog from 'posthog-js';

let initialized = false;

export function initAnalytics() {
  if (initialized || typeof window === 'undefined') return;

  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) {
    console.warn('[PostHog] VITE_POSTHOG_KEY not found. Analytics disabled.');
    return;
  }

  // posthog-js v1.x internally rewrites `app.posthog.com` → `us.i.posthog.com`
  // (the regional dedicated host). It's cleaner to point directly at the
  // regional host so the SDK doesn't have to rewrite and there's no chance
  // of misdirection. PostHog Cloud US shared-host projects use
  // `us.i.posthog.com`; EU shared-host projects use `eu.i.posthog.com`.
  // We default to US — switch if your project is on PostHog Cloud EU.
  posthog.init(key, {
    api_host: 'https://us.i.posthog.com',
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    loaded: (ph) => {
      // eslint-disable-next-line no-console
      console.info('[PostHog] initialized', {
        host: ph.config.api_host,
        key_prefix: key.slice(0, 6) + '…',
      });
    },
  });

  // posthog-js v1.x no longer attaches to window.posthog by default
  // (anti-adblock measure). Re-attach it for local debugging — open
  // DevTools and inspect `window.posthog` to verify init.
  if (typeof window !== 'undefined') {
    (window as unknown as { posthog: typeof posthog }).posthog = posthog;
  }

  initialized = true;
}

export function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (typeof window !== 'undefined' && initialized) {
    posthog.capture(event, properties);
  }
}

export function trackToolOpen(toolId: string) {
  trackEvent('tool_open', { tool_id: toolId });
}

/**
 * Track a tool execution (a button click that produces output).
 *
 * @param toolId  Stable tool slug (e.g. `json-formatter`).
 * @param action  Optional verb describing the action, e.g. `format`, `minify`,
 *                `validate`, `decode`, `generate-v4`, `compute`. Default `execute`.
 * @param durationMs  Optional execution duration in milliseconds (for tools that
 *                   can measure it cheaply).
 */
export function trackToolExecute(toolId: string, action: string = 'execute', durationMs?: number) {
  trackEvent('tool_execute', {
    tool_id: toolId,
    action,
    duration_ms: durationMs,
  });
}

export function trackCopy(toolId: string) {
  trackEvent('tool_copy', { tool_id: toolId });
}

export function trackDownload(toolId: string) {
  trackEvent('tool_download', { tool_id: toolId });
}

export function trackClear(toolId: string) {
  trackEvent('tool_clear', { tool_id: toolId });
}

export function trackSearch(query: string, resultsCount: number) {
  trackEvent('search', {
    query,
    results_count: resultsCount,
  });
}

export function trackPageView(url: string) {
  if (typeof window !== 'undefined' && initialized) {
    posthog.capture('$pageview', { url });
  }
}